import type { Brand } from './brand'

/**
 * Canonical internal units (D-017).
 *
 * Weight is stored in kilograms and every circumference in centimetres,
 * regardless of what the user chose to see. Conversion happens only at the
 * input and display boundaries — never in storage, calculations, or exports.
 *
 * These are branded so a bare `number` cannot reach a function expecting
 * canonical units. That turns a documentation rule into a compile error.
 */
export type Kilograms = Brand<number, 'Kilograms'>
export type Centimetres = Brand<number, 'Centimetres'>
/**
 * A signed difference between two weights.
 *
 * Distinct from `Kilograms` because a change can be negative and zero, while an
 * absolute body weight cannot. Keeping them apart stops a delta being rendered
 * or stored as though it were a weight.
 */
export type KilogramsDelta = Brand<number, 'KilogramsDelta'>

/**
 * A signed difference between two circumferences — a spread or a change.
 *
 * Separate from `Centimetres` for the same reason as `KilogramsDelta`: a
 * difference may be zero, an absolute circumference may not.
 */
export type CentimetresDelta = Brand<number, 'CentimetresDelta'>
export type Inches = Brand<number, 'Inches'>
export type Pounds = Brand<number, 'Pounds'>
/** A percentage expressed 0–100, not 0–1. */
export type Percent = Brand<number, 'Percent'>

const CM_PER_INCH = 2.54
const KG_PER_POUND = 0.45359237

function isFinitePositive(value: number): boolean {
  return Number.isFinite(value) && value > 0
}

/** Construct canonical kilograms. Returns undefined for non-finite or non-positive input. */
export function toKilograms(value: number): Kilograms | undefined {
  return isFinitePositive(value) ? (value as Kilograms) : undefined
}

/** Construct canonical centimetres. Returns undefined for non-finite or non-positive input. */
export function toCentimetres(value: number): Centimetres | undefined {
  return isFinitePositive(value) ? (value as Centimetres) : undefined
}

export function toPercent(value: number): Percent | undefined {
  return Number.isFinite(value) ? (value as Percent) : undefined
}

/** Construct a signed weight difference. Zero and negatives are valid. */
export function toKilogramsDelta(value: number): KilogramsDelta | undefined {
  return Number.isFinite(value) ? (value as KilogramsDelta) : undefined
}

/* Boundary conversions. These are the only place raw numbers become branded
   values or vice versa. */

export function centimetresToInches(value: Centimetres): Inches {
  return (value / CM_PER_INCH) as Inches
}

export function inchesToCentimetres(value: Inches): Centimetres {
  return (value * CM_PER_INCH) as Centimetres
}

export function kilogramsToPounds(value: Kilograms): Pounds {
  return (value / KG_PER_POUND) as Pounds
}

export function poundsToKilograms(value: Pounds): Kilograms {
  return (value * KG_PER_POUND) as Kilograms
}

/** Escape hatch for serialization only. Never use this to feed a calculation. */
export function unwrapNumber(
  value: Kilograms | Centimetres | Inches | Pounds | Percent | KilogramsDelta | CentimetresDelta,
): number {
  return value as number
}

/** Construct a signed circumference difference. Zero and negatives are valid. */
export function toCentimetresDelta(value: number): CentimetresDelta | undefined {
  return Number.isFinite(value) ? (value as CentimetresDelta) : undefined
}
